import EventHandler from '@pixelunion/events';
import themeUtils from '../themeUtils';
import StaticNavigation from './StaticNavigation';
import PredictiveSearch from '../components/PredictiveSearch';
import SearchTakeover from '../components/SearchTakeover';

export default class StaticHeader extends StaticNavigation {
  constructor(section) {
    super(section);

    this.el = section.el;

    this.headerEvents = new EventHandler();

    this.mainHeader = this.el.querySelector('[data-header-main]');
    this.headerContent = this.el.querySelector('[data-header-content]');
    this.navigationWrapper = this.el.querySelector('[data-navigation-wrapper]');

    if (!this.navigationWrapper) return;

    if (themeUtils.isLessThanLarge()) {
      this.navigationContent = this.el.querySelector('.navigation-mobile[data-navigation-content]');
    } else {
      this.navigationContent = this.el.querySelector('.navigation-desktop[data-navigation-content]');
    }

    this.branding = this.el.querySelector('[data-header-branding]');
    this.headerTools = this.el.querySelector('.header-tools');

    this.headerRight = this.el.querySelector('[data-header-content-right]');

    this.cachedHeight = 0;
    this.cachedStickyHeight = 0;
    this.isCacheStale = true;

    this.search = this.el.querySelector('[data-header-search-toggle]');
    this.searchTakeoverEl = this.el.querySelector('[data-search-takeover]');
    this.searchTakeover = new SearchTakeover(this.searchTakeoverEl);
    this.headerEvents.register(this.search, 'click', () => this.searchTakeover.open());

    this.enablePredictiveSearch = section.data.enable_predictive_search;
    this.headerSearchForms = this.el.querySelectorAll('[data-search-form]');

    if (this.enablePredictiveSearch === 'true') {
      this.headerSearchForms.forEach(el => new PredictiveSearch({ el }));
    }

    this.isStickyHeader = section.data.stickyHeader;
    this.isCompactCenter = section.data.compactCenter === 'compact-center';
    this.navHeight = 0;
    this.windowWidth = themeUtils.windowWidth();
    setTimeout(() => {
      this.navHeight = this.navigationWrapper.getBoundingClientRect().height;
    },
    100);
    this._setupBranding();
    this._setCompactCenterHeights();
    this._toggleStickyHeader(this._shouldHeaderStick());
    this._bindEvents();
    this.reloadNavigation();
  }

  onSectionUnload() {
    super.onSectionUnload();
    window.removeEventListener('resize', this.resizeHeader);
    window.removeEventListener('scroll', this.scrollHeader);
    this._toggleStickyHeader(this._shouldHeaderStick());
  }

  _bindEvents() {
    this.resizeHeader = () => {
      // Prevent resize caused by iOS viewport change
      if (themeUtils.windowWidth() === this.windowWidth) { return; }
      this.windowWidth = themeUtils.windowWidth();

      this.navHeight = this.navigationWrapper.getBoundingClientRect().height;

      this.isCacheStale = true;

      this._toggleStickyHeader(this._shouldHeaderStick());
      this._setCompactCenterHeights();
    };
    this.scrollHeader = () => {
      // Only calculate heights on scroll if sticky header is disabled
      if (!this.isStickyHeader) {
        this._setCompactCenterHeights();
      }

      // Sticky header calculates heights
      this._toggleStickyHeader(this._shouldHeaderStick());
    };
    window.addEventListener('resize', this.resizeHeader);
    window.addEventListener('scroll', this.scrollHeader);
  }

  _shouldHeaderStick() {
    let shouldStick = false;

    if (!this.isStickyHeader) {
      return shouldStick;
    }

    if (this.isCacheStale) {
      let height = document.body.classList.contains('sticky-header') ? 0 : this.headerContent.getBoundingClientRect().height;
      let stickyHeight = document.body.classList.contains('sticky-header') ? this.headerContent.getBoundingClientRect().height : 0;

      document.body.classList.toggle('sticky-header');

      height = !document.body.classList.contains('sticky-header') ? this.headerContent.getBoundingClientRect().height : height;
      stickyHeight = !document.body.classList.contains('sticky-header') ? stickyHeight : this.headerContent.getBoundingClientRect().height;

      document.body.classList.toggle('sticky-header');

      this.cachedHeight = height;
      this.cachedStickyHeight = stickyHeight;
      this.isCacheStale = false;
    }

    if (window.scrollY > ((this.headerTools.getBoundingClientRect().height + this.cachedHeight) - this.cachedStickyHeight)) {
      shouldStick = true;
    }

    if (themeUtils.isLessThanLarge()) {
      shouldStick = false;
    }

    return shouldStick;
  }

  _toggleStickyHeader(toggleOn) {
    let paddingBottom;
    if (toggleOn == null) { toggleOn = false; }
    if (toggleOn && !document.body.classList.contains('sticky-header')) {
      paddingBottom = this.cachedHeight;
      this.mainHeader.style.paddingBottom = `${paddingBottom}px`;
    }

    if (!toggleOn) {
      paddingBottom = 0;
      this.mainHeader.style.paddingBottom = `${paddingBottom}px`;
    }

    if (toggleOn !== document.body.classList.contains('sticky-header')) {
      if (toggleOn) {
        document.body.classList.add('sticky-header');
      } else {
        document.body.classList.remove('sticky-header');
      }
      this._setCompactCenterHeights(!toggleOn);
    }
  }

  /*
        Match Navigation and Branding heights
        Center navigation vertically if Branding is taller
        */
  _setCompactCenterHeights(toggleOn) {
    if (toggleOn == null) { toggleOn = true; }
    if (!this.isCompactCenter) { return; }

    let minHeight = 'auto';

    // Get height of inner navigation to prevent height locking on two row navigation
    const navHeight = this.navigationContent.getBoundingClientRect().height;
    let navOffset = 'auto';

    if (!themeUtils.isLessThanLarge() && toggleOn) {
      const logoImg = this.branding.querySelector('img');
      if (logoImg && !logoImg.complete) {
        this.headerEvents.register(logoImg, 'load', () => this._setCompactCenterHeights());
      }
      const heights = [];
      heights.push(navHeight);
      heights.push(this.branding.getBoundingClientRect().height);

      minHeight = Math.max.apply(null, heights);
    }

    if (navHeight < minHeight) {
      navOffset = (minHeight - navHeight) / 2;
    }

    this.navHeight = this.navigationWrapper.getBoundingClientRect().height;

    if (this.isStickyHeader && document.body.classList.contains('sticky-header')) {
      this.headerContent.style.minHeight = 'auto';
      this.navigationWrapper.style.marginTop = 0;
    } else {
      this.headerContent.style.minHeight = `${minHeight}px`;
      this.navigationWrapper.style.marginTop = `${navOffset}px`;
    }
  }

  _setupBranding() {
    // Clone desktop branding to mobile
    const mobileBranding = this.branding.cloneNode(true);

    mobileBranding.removeAttribute('data-header-branding');
    mobileBranding.classList.remove('header-branding-desktop');
    mobileBranding.classList.add('header-branding-mobile');

    this.navigationContent.insertBefore(mobileBranding, this.navigationContent.firstChild);
  }
}
